import React, { useEffect, useState } from 'react';
import { useGasI18n, useGasNotify } from '../../shared/kit';
import { addItem, cartCount, subscribe } from '../rental-cart/store';

/**
 * Contrôles panier de location.
 *  - variante `floating` (défaut) : badge flottant en bas à droite, visible
 *    seulement quand le panier n'est pas vide. Branche aussi les boutons
 *    « Ajouter à ma location » (SSR) sur le catalogue et les fiches.
 *  - variante `inline` : badge intégré dans le header (bloc « Panier location »),
 *    toujours visible, compteur affiché quand > 0.
 *
 * Plusieurs badges peuvent être montés simultanément (header + flottant) : la
 * délégation de clic « Ajouter » est attachée UNE SEULE fois (flag module).
 */

// Délégation globale attachée une seule fois, quel que soit le nombre de montages.
let clickDelegationAttached = false;

export default function App({ cartUrl, variant = 'floating' }: { cartUrl: string; variant?: 'floating' | 'inline' }) {
    const { t } = useGasI18n();
    const notify = useGasNotify();
    const [count, setCount] = useState(0);
    const isInline = variant === 'inline';

    useEffect(() => {
        setCount(cartCount());
        const unsub = subscribe(() => setCount(cartCount()));

        if (clickDelegationAttached) {
            return () => { unsub(); };
        }
        clickDelegationAttached = true;

        // Délégation : tout clic sur un bouton [data-rental-add] ajoute au panier.
        const onClick = (e: MouseEvent) => {
            const btn = (e.target as HTMLElement)?.closest<HTMLElement>('[data-rental-add]');
            if (!btn) return;
            e.preventDefault();
            const id = Number(btn.dataset.itemId || '0');
            if (!id) return;
            const qtyInput = btn.parentElement?.querySelector<HTMLInputElement>('[data-rental-qty]');
            const qty = qtyInput ? Math.max(1, Number(qtyInput.value) || 1) : 1;
            addItem({
                item_id: id,
                name: btn.dataset.name || `#${id}`,
                slug: btn.dataset.slug || '',
                image_url: btn.dataset.image || null,
                free: btn.dataset.free === '1',
                qty,
            });
            notify.success(t('rental.cart.added', { name: btn.dataset.name || '' }));
        };
        document.addEventListener('click', onClick);
        return () => {
            unsub();
            document.removeEventListener('click', onClick);
            clickDelegationAttached = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Flottant : masqué si panier vide. Inline (header) : toujours affiché.
    if (!isInline && count <= 0) {
        return null;
    }

    const className = 'gas-rental-cartbadge' + (isInline ? ' gas-rental-cartbadge--inline' : '');

    return (
        <a className={className} href={cartUrl}>
            <span className="gas-rental-cartbadge__icon" aria-hidden="true">🧰</span>
            <span className="gas-rental-cartbadge__label">{t('rental.cart.my_rental')}</span>
            {count > 0 && <span className="gas-rental-cartbadge__count">{count}</span>}
        </a>
    );
}
